// Logging surfaces. Everything here produces events; nothing writes a read model.
import { useMemo, useState } from "react";
import { Check, Plus, Repeat } from "lucide-react";
import { useExercises, useLogEvent, useProfile } from "../lib/api";
import type { Exercise, NewEvent } from "../lib/types";
import { Button, Chip, Field, NumberInput, Sheet } from "../components/ui";

type Mode = "menu" | "lift" | "swim" | "boxing" | "conditioning" | "mobility" | "bodycomp" | "note";

export function Log({ userId, onDone }: { userId: string; onDone: () => void }) {
  const [mode, setMode] = useState<Mode>("menu");
  const log = useLogEvent(userId);

  async function submit(events: NewEvent | NewEvent[]) {
    await log.mutateAsync(events);
    setMode("menu");
    onDone();
  }

  return (
    <div className="grid gap-4">
      <h1 className="text-3xl font-semibold tracking-tight">Log</h1>
      {mode === "menu" && (
        <section className="grid grid-cols-2 gap-3">
          <Tile title="Lift" sub="Sets, kg, reps" onClick={() => setMode("lift")} />
          <Tile title="Swim" sub="Metres and time" onClick={() => setMode("swim")} />
          <Tile title="Boxing" sub="Class, bag, PT" onClick={() => setMode("boxing")} />
          <Tile
            title="Conditioning"
            sub="Erg, ropes, sled"
            onClick={() => setMode("conditioning")}
          />
          <Tile title="Mobility" sub="Minutes" onClick={() => setMode("mobility")} />
          <Tile title="Body comp" sub="Scale / InBody" onClick={() => setMode("bodycomp")} />
        </section>
      )}
      {mode === "lift" && (
        <LiftLogger onSubmit={submit} onCancel={() => setMode("menu")} busy={log.isPending} />
      )}
      {mode === "swim" && (
        <SwimLogger onSubmit={submit} onCancel={() => setMode("menu")} busy={log.isPending} />
      )}
      {mode === "boxing" && (
        <BoxingLogger onSubmit={submit} onCancel={() => setMode("menu")} busy={log.isPending} />
      )}
      {mode === "conditioning" && (
        <ConditioningLogger
          onSubmit={submit}
          onCancel={() => setMode("menu")}
          busy={log.isPending}
        />
      )}
      {mode === "mobility" && (
        <MobilityLogger onSubmit={submit} onCancel={() => setMode("menu")} busy={log.isPending} />
      )}
      {mode === "bodycomp" && (
        <BodycompLogger onSubmit={submit} onCancel={() => setMode("menu")} busy={log.isPending} />
      )}
      {log.error && <p className="text-sm text-danger">{(log.error as Error).message}</p>}
    </div>
  );
}

function Tile({ title, sub, onClick }: { title: string; sub: string; onClick: () => void }) {
  return (
    <button onClick={onClick} className="tap card p-4 text-left transition active:scale-[0.98]">
      <p className="text-base font-semibold">{title}</p>
      <p className="text-xs text-ink-mute">{sub}</p>
    </button>
  );
}

interface LoggerProps {
  onSubmit: (e: NewEvent | NewEvent[]) => Promise<void>;
  onCancel: () => void;
  busy: boolean;
}

function nowIso(minutesAgo = 0) {
  return new Date(Date.now() - minutesAgo * 60_000).toISOString();
}

// ── Lift: the surface that has to be fast ────────────────────────────────────

interface SetRow {
  id: string;
  exercise: Exercise;
  weight_kg: number;
  reps: number;
  rir: number | "";
  set_type: "normal" | "warmup";
  at: string;
}

function LiftLogger({ onSubmit, onCancel, busy }: LoggerProps) {
  const { data: exercises } = useExercises();
  const { data: profile } = useProfile();
  const [sessionId] = useState(() => crypto.randomUUID());
  const [sets, setSets] = useState<SetRow[]>([]);
  const [picking, setPicking] = useState(false);
  const [exercise, setExercise] = useState<Exercise | null>(null);
  const [weight, setWeight] = useState<number | "">("");
  const [reps, setReps] = useState<number | "">("");
  const [rir, setRir] = useState<number | "">("");
  const [warmup, setWarmup] = useState(false);
  const [title, setTitle] = useState("");
  const injuryOk = profile?.flags.injury_ok ?? false;
  const visible = useMemo(
    () => (exercises ?? []).filter((e) => injuryOk || !e.lower_body),
    [exercises, injuryOk],
  );

  function addSet() {
    if (!exercise || reps === "" || reps <= 0) return;
    const row: SetRow = {
      id: crypto.randomUUID(),
      exercise,
      weight_kg: weight === "" ? 0 : weight,
      reps,
      rir,
      set_type: warmup ? "warmup" : "normal",
      at: nowIso(),
    };
    setSets((s) => [...s, row]);
    setWarmup(false);
  }

  function repeatLast() {
    const last = sets[sets.length - 1];
    if (!last) return;
    setSets((s) => [...s, { ...last, id: crypto.randomUUID(), at: nowIso() }]);
  }

  async function finish() {
    const working = sets.filter((s) => s.set_type === "normal");
    const events: NewEvent[] = sets.map((s) => ({
      type: "set.completed",
      occurred_at: s.at,
      session_id: sessionId,
      payload: {
        exercise_id: s.exercise.id,
        exercise: s.exercise.name,
        pattern: s.exercise.pattern,
        push: s.exercise.pattern === "push_h" || s.exercise.pattern === "push_v",
        pull: s.exercise.pattern === "pull_h" || s.exercise.pattern === "pull_v",
        lower_body: s.exercise.lower_body,
        equipment: s.exercise.equipment,
        weight_kg: s.weight_kg,
        reps: s.reps,
        rir: s.rir === "" ? null : s.rir,
        set_type: s.set_type,
      },
    }));
    if (sets.length) {
      events.push({
        type: "session.completed",
        occurred_at: nowIso(),
        session_id: sessionId,
        payload: {
          kind: "lift",
          title: title.trim() || "Lifting",
          sets: working.length,
          tonnage_kg: working.reduce((n, s) => n + s.weight_kg * s.reps, 0),
          duration_s: sets.length
            ? Math.max(60, (Date.now() - new Date(sets[0].at).getTime()) / 1000)
            : 0,
        },
      });
    }
    await onSubmit(events);
  }

  const tonnage = sets
    .filter((s) => s.set_type === "normal")
    .reduce((n, s) => n + s.weight_kg * s.reps, 0);

  return (
    <div className="grid gap-3">
      <input
        className="field focus:field-focus"
        placeholder="Session name (optional)"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
      />

      <div className="card grid gap-3 p-4">
        <button onClick={() => setPicking(true)} className="tap field text-left focus:field-focus">
          {exercise ? (
            <span className="flex items-center justify-between">
              <span>{exercise.name}</span>
              <Chip>{exercise.pattern.replace("_", " ")}</Chip>
            </span>
          ) : (
            <span className="text-ink-mute">Choose an exercise…</span>
          )}
        </button>
        <div className="grid grid-cols-3 gap-2">
          <Field label="kg">
            <NumberInput value={weight} onChange={setWeight} step={0.5} placeholder="0" />
          </Field>
          <Field label="reps">
            <NumberInput value={reps} onChange={setReps} inputMode="numeric" placeholder="0" />
          </Field>
          <Field label="RIR">
            <NumberInput value={rir} onChange={setRir} inputMode="numeric" placeholder="–" />
          </Field>
        </div>
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm text-ink-soft">
            <input type="checkbox" checked={warmup} onChange={(e) => setWarmup(e.target.checked)} />{" "}
            warm-up
          </label>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={repeatLast} disabled={!sets.length}>
              <Repeat size={16} />
            </Button>
            <Button onClick={addSet} disabled={!exercise || reps === "" || reps <= 0}>
              <Plus size={16} className="mr-1 inline" />
              Add set
            </Button>
          </div>
        </div>
      </div>

      {sets.length > 0 && (
        <ul className="card divide-y divide-border">
          {sets.map((s, i) => (
            <li key={s.id} className="flex items-center gap-3 px-4 py-2.5 text-sm">
              <span className="numeral w-5 text-ink-mute">{i + 1}</span>
              <span className="min-w-0 flex-1 truncate">{s.exercise.name}</span>
              {s.set_type === "warmup" && <Chip>warm-up</Chip>}
              <span className="numeral">
                {s.weight_kg} × {s.reps}
              </span>
              <button
                aria-label="Remove"
                className="tap text-ink-mute"
                onClick={() => setSets((x) => x.filter((y) => y.id !== s.id))}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="flex items-center justify-between">
        <p className="text-sm text-ink-mute">
          <span className="numeral">{sets.filter((s) => s.set_type === "normal").length}</span>{" "}
          working sets · <span className="numeral">{tonnage.toLocaleString("en-GB")}</span> kg
        </p>
        <div className="flex gap-2">
          <Button variant="ghost" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={() => void finish()} disabled={!sets.length || busy}>
            <Check size={16} className="mr-1 inline" />
            Finish
          </Button>
        </div>
      </div>

      <ExercisePicker
        open={picking}
        exercises={visible}
        onPick={(e) => {
          setExercise(e);
          setPicking(false);
        }}
        onClose={() => setPicking(false)}
      />
    </div>
  );
}

function ExercisePicker({
  open,
  exercises,
  onPick,
  onClose,
}: {
  open: boolean;
  exercises: Exercise[];
  onPick: (e: Exercise) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState("");
  const list = exercises.filter((e) => e.name.toLowerCase().includes(q.toLowerCase()));
  const groups = new Map<string, Exercise[]>();
  for (const e of list) groups.set(e.pattern, [...(groups.get(e.pattern) ?? []), e]);
  return (
    <Sheet open={open} onClose={onClose} title="Exercise">
      <input
        autoFocus
        className="field mb-3 focus:field-focus"
        placeholder="Search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      <div className="grid gap-3">
        {[...groups.entries()].map(([pattern, list]) => (
          <div key={pattern}>
            <p className="mb-1 text-xs uppercase tracking-widest text-ink-mute">
              {pattern.replace("_", " ")}
            </p>
            <ul className="grid gap-1">
              {list.map((e) => (
                <li key={e.id}>
                  <button
                    onClick={() => onPick(e)}
                    className="tap w-full rounded-chip px-3 py-2 text-left hover:bg-surface-2"
                  >
                    {e.name} <span className="text-xs text-ink-mute">· {e.equipment}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </Sheet>
  );
}

// ── Simple loggers ───────────────────────────────────────────────────────────

function Frame({
  title,
  children,
  onCancel,
  onSave,
  canSave,
  busy,
}: {
  title: string;
  children: React.ReactNode;
  onCancel: () => void;
  onSave: () => void;
  canSave: boolean;
  busy: boolean;
}) {
  return (
    <div className="card grid gap-3 p-4">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
      <div className="flex justify-end gap-2">
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={onSave} disabled={!canSave || busy}>
          Save
        </Button>
      </div>
    </div>
  );
}

const POOLS = [
  { id: "25yd", label: "25 yd", metres: 22.86 },
  { id: "25m", label: "25 m", metres: 25 },
  { id: "50m", label: "50 m", metres: 50 },
] as const;

function SwimLogger({ onSubmit, onCancel, busy }: LoggerProps) {
  const [pool, setPool] = useState<(typeof POOLS)[number]["id"]>(() => {
    try {
      return (localStorage.getItem("hearth.pool") as (typeof POOLS)[number]["id"]) || "25yd";
    } catch {
      return "25yd";
    }
  });
  const [lengths, setLengths] = useState<number | "">("");
  const [minutes, setMinutes] = useState<number | "">("");
  const poolLen = POOLS.find((p) => p.id === pool)!.metres;
  const distance = lengths === "" ? 0 : Math.round(lengths * poolLen);
  return (
    <Frame
      title="Swim"
      onCancel={onCancel}
      busy={busy}
      canSave={(lengths !== "" && lengths > 0) || (minutes !== "" && minutes > 0)}
      onSave={() =>
        void onSubmit({
          type: "swim.completed",
          occurred_at: nowIso(),
          payload: {
            distance_m: distance,
            lengths: lengths || 0,
            pool,
            duration_s: (minutes || 0) * 60,
          },
        })
      }
    >
      <div className="flex gap-2">
        {POOLS.map((p) => (
          <button
            key={p.id}
            onClick={() => {
              setPool(p.id);
              try {
                localStorage.setItem("hearth.pool", p.id);
              } catch {
                /* private mode */
              }
            }}
            className={`tap rounded-chip px-3 py-1.5 text-sm ${pool === p.id ? "bg-accent text-page" : "bg-surface-2 text-ink-soft"}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Field label="lengths">
          <NumberInput
            value={lengths}
            onChange={setLengths}
            inputMode="numeric"
            step={2}
            placeholder="40"
          />
        </Field>
        <Field label="minutes">
          <NumberInput value={minutes} onChange={setMinutes} inputMode="numeric" />
        </Field>
      </div>
      <p className="text-sm text-ink-mute">
        <span className="numeral">{distance.toLocaleString("en-GB")}</span> m
        {lengths === 40 && pool === "25yd" ? " — your usual session" : ""}
      </p>
    </Frame>
  );
}

function BoxingLogger({ onSubmit, onCancel, busy }: LoggerProps) {
  const [kind, setKind] = useState<"class" | "bag" | "pt">("class");
  const [minutes, setMinutes] = useState<number | "">("");
  return (
    <Frame
      title="Boxing"
      onCancel={onCancel}
      busy={busy}
      canSave={minutes !== "" && minutes > 0}
      onSave={() =>
        void onSubmit({
          type: "boxing.completed",
          occurred_at: nowIso(),
          payload: { kind, duration_s: (minutes || 0) * 60 },
        })
      }
    >
      <div className="flex gap-2">
        {(["class", "bag", "pt"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`tap rounded-chip px-3 py-1.5 text-sm ${kind === k ? "bg-accent text-page" : "bg-surface-2 text-ink-soft"}`}
          >
            {k === "pt" ? "PT" : k}
          </button>
        ))}
      </div>
      <Field label="minutes">
        <NumberInput value={minutes} onChange={setMinutes} inputMode="numeric" />
      </Field>
    </Frame>
  );
}

function ConditioningLogger({ onSubmit, onCancel, busy }: LoggerProps) {
  const [kind, setKind] = useState<
    "ski_erg" | "ropes" | "row_erg" | "bike" | "sled" | "carry" | "other"
  >("ski_erg");
  const [minutes, setMinutes] = useState<number | "">("");
  const [rounds, setRounds] = useState<number | "">("");
  const [load, setLoad] = useState<number | "">("");
  const { data: profile } = useProfile();
  const injuryOk = profile?.flags.injury_ok ?? false;
  const kinds = (["ski_erg", "ropes", "row_erg", "bike", "sled", "carry", "other"] as const).filter(
    (k) => injuryOk || !["sled", "carry", "bike", "row_erg"].includes(k),
  );
  const payload: Record<string, unknown> = {
    kind,
    duration_s: (minutes || 0) * 60,
    load_kg: load || null,
  };
  if (kind === "sled") payload.sled_rounds = rounds || 0;
  if (kind === "carry") payload.carry_trips = rounds || 0;
  return (
    <Frame
      title="Conditioning"
      onCancel={onCancel}
      busy={busy}
      canSave={(minutes !== "" && minutes > 0) || (rounds !== "" && rounds > 0)}
      onSave={() =>
        void onSubmit({ type: "conditioning.completed", occurred_at: nowIso(), payload })
      }
    >
      <div className="flex flex-wrap gap-2">
        {kinds.map((k) => (
          <button
            key={k}
            onClick={() => setKind(k)}
            className={`tap rounded-chip px-3 py-1.5 text-sm ${kind === k ? "bg-accent text-page" : "bg-surface-2 text-ink-soft"}`}
          >
            {k.replace("_", " ")}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Field label="minutes">
          <NumberInput value={minutes} onChange={setMinutes} inputMode="numeric" />
        </Field>
        <Field label={kind === "carry" ? "trips" : "rounds"}>
          <NumberInput value={rounds} onChange={setRounds} inputMode="numeric" />
        </Field>
        <Field label="load kg">
          <NumberInput value={load} onChange={setLoad} step={0.5} />
        </Field>
      </div>
    </Frame>
  );
}

function MobilityLogger({ onSubmit, onCancel, busy }: LoggerProps) {
  const [minutes, setMinutes] = useState<number | "">("");
  const [focus, setFocus] = useState("");
  return (
    <Frame
      title="Mobility"
      onCancel={onCancel}
      busy={busy}
      canSave={minutes !== "" && minutes > 0}
      onSave={() =>
        void onSubmit({
          type: "mobility.completed",
          occurred_at: nowIso(),
          payload: { duration_s: (minutes || 0) * 60, focus: focus.trim() || null },
        })
      }
    >
      <div className="grid grid-cols-2 gap-2">
        <Field label="minutes">
          <NumberInput value={minutes} onChange={setMinutes} inputMode="numeric" />
        </Field>
        <Field label="focus">
          <input
            className="field focus:field-focus"
            placeholder="hips, shoulders…"
            value={focus}
            onChange={(e) => setFocus(e.target.value)}
          />
        </Field>
      </div>
    </Frame>
  );
}

function BodycompLogger({ onSubmit, onCancel, busy }: LoggerProps) {
  const [weight, setWeight] = useState<number | "">("");
  const [fat, setFat] = useState<number | "">("");
  const [smm, setSmm] = useState<number | "">("");
  return (
    <Frame
      title="Body composition"
      onCancel={onCancel}
      busy={busy}
      canSave={weight !== "" && weight > 0}
      onSave={() =>
        void onSubmit({
          type: "bodycomp.recorded",
          occurred_at: nowIso(),
          payload: {
            weight_kg: weight,
            body_fat_pct: fat || null,
            skeletal_muscle_kg: smm || null,
            source_device: "manual",
          },
        })
      }
    >
      <div className="grid grid-cols-3 gap-2">
        <Field label="weight kg">
          <NumberInput value={weight} onChange={setWeight} step={0.1} />
        </Field>
        <Field label="body fat %">
          <NumberInput value={fat} onChange={setFat} step={0.1} />
        </Field>
        <Field label="SMM kg">
          <NumberInput value={smm} onChange={setSmm} step={0.1} />
        </Field>
      </div>
    </Frame>
  );
}
